/**
 * Stat-card group — the row of summary cards that heads a grid page or a
 * dashboard, in one shared surface so every page behaves the same on a phone.
 *
 * Above the group's carousel breakpoint each page keeps its own column count.
 * Below it the same cards become a swipeable, snapped scroller: one usable card
 * wide with the next one peeking, keyboard-scrollable, and with no arrows or
 * dots to compete with the cards for space.
 *
 * Contains:
 * - StatCardGroup — the group/carousel container (default export)
 */

import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { buildStatCardGroupSx, DEFAULT_CAROUSEL_BREAKPOINT } from './statCardGroupLayout';

/**
 * @param {object} props
 * @param {React.ReactNode} props.children - the StatCards, as direct children —
 *   the carousel styles its own children, so wrapping them defeats snapping.
 * @param {number|Object<string, number>} [props.columns] - grid-mode card count,
 *   either flat or per MUI breakpoint key (`{ xs: 2, md: 5 }`).
 * @param {number|string} [props.gap] - inter-card spacing, in both modes.
 * @param {string} [props.carouselBelow] - MUI breakpoint key below which the
 *   group becomes a carousel. Defaults to the app's `mobile` phone cutoff;
 *   pages whose group is a
 *   grid only from `md` up pass `md` so the carousel covers the whole width
 *   range they already treat as mobile.
 * @param {object} [props.gridSx] - extra sx applied only in grid mode, for the
 *   groups whose wide-viewport geometry isn't a plain N-column grid.
 * @param {object|Array} [props.sx] - extra sx applied in both modes.
 * @returns {React.ReactElement} The group container.
 */
function StatCardGroup({ children, columns, gap, carouselBelow = DEFAULT_CAROUSEL_BREAKPOINT, gridSx, sx }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const isCarousel = useMediaQuery(theme.breakpoints.down(carouselBelow));

  // A scroll container with no focusable children is unreachable by keyboard
  // unless it takes focus itself; arrow keys then scroll it natively.
  const carouselProps = isCarousel
    ? { role: 'group', tabIndex: 0, 'aria-label': t('general.summaryCards', 'Summary cards') }
    : {};

  return (
    <Box {...carouselProps} sx={[buildStatCardGroupSx({ isCarousel, columns, gap }), !isCarousel && gridSx, sx]}>
      {children}
    </Box>
  );
}

StatCardGroup.propTypes = {
  children: PropTypes.node,
  columns: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  gap: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  carouselBelow: PropTypes.string,
  gridSx: PropTypes.object,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default StatCardGroup;
